// BOM Intelligence: the portfolio, by stage, with what needs handling
// apps/smart-catalog/frontend/src/pages/BomPortfolioPage.tsx
//
// One page for every stage, read top down: the title and actions, the stage
// bar (All or a stage, and in a stage a category: Overview, Sourcing, Changes,
// Health), the same five answers in every stage in one panel of rings
// (schedule, supply, changes, package, cost), then every product in view to
// explore: searchable, paged, in the same row look as the BOM list. The search
// sits with the products because it narrows only them. The numbers come from
// the server (bom-v2 portfolio.service.ts), which says where each one is read
// from. The products carry the caller's star, and the Organize button (shared
// with the BOM list) narrows them to favorites, folds them into folders by
// label, import type or status, each folder summed up in lined-up columns,
// and holds Preview pending.
//
// Rows can be ticked (shift ticks a range, the box by the title ticks all on
// screen). The three dots on a ticked row act on every ticked row: labels,
// stage, export, delete. The ticks clear when the list changes.

import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, ButtonBase, CircularProgress, MenuItem, Pagination, Select, Snackbar, Stack, Typography, alpha } from '@mui/material';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHref, useNavigate, useSearchParams } from 'react-router-dom';

import { usePreviewPendingToggle } from '../components/bom-v2/PreviewPendingToggle';
import {
  FavoritesEmpty,
  FolderColumns,
  FolderHeader,
  OrganizeButton,
  folderName,
  useFoldedGroups,
  useOrganizeSettings,
} from '../components/bom/organize/BomOrganize';
import BomRowMenu from '../components/bom/organize/BomRowMenu';
import DeleteBomDialog, { DeleteBomsDialog } from '../components/bom/DeleteBomDialog';
import EditBomDialog from '../components/bom/EditBomDialog';
import { exportBomToExcel, type BomExportKind } from '../components/bom-v2/bomExcelExport';
import bomV2Service from '../services/bomV2Service';
import { HeadlinePanel } from '../components/portfolio/HeadlineCard';
import { PillarTabs, ProductRowCard, SearchField, SelectTick, StageBar, T } from '../components/portfolio/PortfolioParts';
import { useBreadcrumbUpdater } from '@ciea.ai/smart-infra-navigation';
import { useDebounced } from '../hooks/useDebounced';
import { useSetBomFavorite } from '../hooks/useBomV2';
import { NO_GROUP_KEY, type BomV2 } from '../types/bomV2';
import portfolioService, { type Pillar, type ProductRow, type StageId, type StageKey } from '../services/portfolioService';

/** Rows per page to choose from; the first one below is the default. */
const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE = 50;
/** The bill tab each category opens. */
const BILL_TAB: Record<Pillar, string> = { overview: 'summary', sourcing: 'sourcing', changes: 'changes', health: 'package' };
/** How many bills a selection action works on at a time. */
const BATCH = 6;

/** Run `fn` on every item, a few at a time; answers the failures by item. */
async function eachSettled<T>(items: T[], fn: (item: T) => Promise<unknown>): Promise<Array<{ item: T; message: string }>> {
  const failed: Array<{ item: T; message: string }> = [];
  for (let i = 0; i < items.length; i += BATCH) {
    const chunk = items.slice(i, i + BATCH);
    const results = await Promise.allSettled(chunk.map(fn));
    results.forEach((r, k) => {
      if (r.status === 'rejected') {
        const err = r.reason as { response?: { data?: { message?: string } }; message?: string };
        failed.push({ item: chunk[k], message: err?.response?.data?.message || err?.message || 'failed' });
      }
    });
  }
  return failed;
}

const BomPortfolioPage: React.FC = () => {
  useBreadcrumbUpdater([{ path: '/bom', breadcrumb: 'Bill of Materials' }]);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const stage = (params.get('stage') as StageKey) || 'ALL';
  const pillar = (stage === 'ALL' ? 'overview' : (params.get('pillar') as Pillar)) || 'overview';
  const [preview, setPreview] = usePreviewPendingToggle();
  // The products' search: one field, matched against name, number,
  // contractor, manager and step.
  const [search, setSearch] = useState('');
  const pn = '';
  const q = useDebounced(search.trim(), 250);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE);
  const [page, setPage] = useState(1);
  const [organize, setOrganizeSettings] = useOrganizeSettings();
  const [isFolded, toggleFold] = useFoldedGroups();
  const groupBy = organize.groupBy === 'none' ? undefined : organize.groupBy;
  const favorites = organize.favoritesOnly;
  const setOrganize = (patch: Parameters<typeof setOrganizeSettings>[0]) => {
    setOrganizeSettings(patch);
    setPage(1);
  };
  const setFavorite = useSetBomFavorite();
  // The row menu (three dots) and what it opens.
  const qc = useQueryClient();
  const billHref = useHref('/bom');
  const [menu, setMenu] = useState<{ anchor: HTMLElement; row: ProductRow } | null>(null);
  const [editing, setEditing] = useState<BomV2 | null>(null);
  const [deleting, setDeleting] = useState<ProductRow | null>(null);
  const [deletingMany, setDeletingMany] = useState<ProductRow[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const refreshPortfolio = () => qc.invalidateQueries({ queryKey: ['portfolio'] });
  // Ticked rows, by bill id; the last one ticked anchors a shift range.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const lastTicked = useRef<string | null>(null);

  const setView = (next: { stage?: StageKey; pillar?: Pillar }) => {
    const p = new URLSearchParams(params);
    if (next.stage) {
      if (next.stage === 'ALL') p.delete('stage');
      else p.set('stage', next.stage);
    }
    const nextPillar = next.pillar ?? (next.stage && next.stage !== stage ? 'overview' : pillar);
    if (nextPillar === 'overview' || (next.stage ?? stage) === 'ALL') p.delete('pillar');
    else p.set('pillar', nextPillar);
    setParams(p, { replace: true });
    setPage(1);
  };

  const { data: summary, isLoading } = useQuery({
    queryKey: ['portfolio', 'summary', stage, pillar, preview],
    queryFn: () => portfolioService.summary(stage, pillar, preview),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
  const { data: products, isFetching: productsLoading } = useQuery({
    queryKey: ['portfolio', 'products', stage, pillar, pn, q, page, pageSize, preview, favorites, groupBy ?? 'none'],
    queryFn: () => portfolioService.products({ stage, pillar, pn, q, offset: (page - 1) * pageSize, limit: pageSize, preview, favorites, groupBy }),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const stageItems = useMemo(() => {
    const s = summary?.stages;
    if (!s) return [];
    return [
      { key: 'ALL' as StageKey, label: 'All', count: s.total },
      ...s.stages.map((x) => ({ key: x.id as StageKey, label: x.name, count: x.count })),
      ...(s.unset ? [{ key: 'UNSET' as StageKey, label: 'No stage', count: s.unset }] : []),
    ];
  }, [summary?.stages]);
  const stageName = stageItems.find((i) => i.key === stage)?.label ?? 'Portfolio';

  const openBill = (bomId: string) => navigate(`/bom/${bomId}`, { state: { restoreTab: BILL_TAB[pillar] } });
  const folders = products?.groups;

  // Every row the list holds, and the ones on screen in order (open folders only, each bill once).
  const rowsById = useMemo(() => {
    const map = new Map<string, ProductRow>();
    for (const r of products?.rows ?? []) map.set(r.bomId, r);
    for (const g of products?.groups ?? []) for (const r of g.rows) map.set(r.bomId, r);
    return map;
  }, [products]);
  const onScreen: string[] = [];
  if (folders && groupBy) {
    const seen = new Set<string>();
    for (const g of folders) {
      if (isFolded(groupBy, g.key)) continue;
      for (const r of g.rows) {
        if (seen.has(r.bomId)) continue;
        seen.add(r.bomId);
        onScreen.push(r.bomId);
      }
    }
  } else for (const r of products?.rows ?? []) onScreen.push(r.bomId);

  // A different list clears the ticks; a bill that left the list (moved, deleted) drops out.
  useEffect(() => {
    setSelected(new Set());
    lastTicked.current = null;
  }, [stage, pillar, q, page, pageSize, preview, favorites, groupBy]);
  useEffect(() => {
    setSelected((prev) => {
      if (!prev.size) return prev;
      const next = new Set([...prev].filter((id) => rowsById.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rowsById]);

  const tick = (bomId: string, range: boolean) => {
    const from = lastTicked.current ? onScreen.indexOf(lastTicked.current) : -1;
    const to = onScreen.indexOf(bomId);
    const on = !selected.has(bomId);
    const ids = range && from >= 0 && to >= 0 ? onScreen.slice(Math.min(from, to), Math.max(from, to) + 1) : [bomId];
    const next = new Set(selected);
    for (const id of ids) {
      if (on) next.add(id);
      else next.delete(id);
    }
    setSelected(next);
    lastTicked.current = bomId;
  };
  const tickedOnScreen = onScreen.filter((id) => selected.has(id)).length;
  const allTicked = onScreen.length > 0 && tickedOnScreen === onScreen.length;
  const clearTicks = () => {
    setSelected(new Set());
    lastTicked.current = null;
  };

  const renderRow = (r: ProductRow) => (
    <ProductRowCard
      key={r.bomId}
      row={r}
      onOpen={() => openBill(r.bomId)}
      onToggleFavorite={() => setFavorite.mutate({ bomId: r.bomId, favorite: !r.isFavorite })}
      onMenu={(anchor) => setMenu({ anchor, row: r })}
      selected={selected.has(r.bomId)}
      onSelect={(range) => tick(r.bomId, range)}
    />
  );
  // The dots on a ticked row act on every ticked row; on any other row, on that row.
  const targets: ProductRow[] = !menu
    ? []
    : selected.has(menu.row.bomId) && selected.size > 1
      ? [...selected].map((id) => rowsById.get(id)).filter((r): r is ProductRow => !!r)
      : [menu.row];
  const sharedStage = targets.length && targets.every((t) => (t.stageId ?? null) === (targets[0].stageId ?? null)) ? (targets[0].stageId ?? null) : undefined;
  const many = (n: number) => `${n} BOM${n === 1 ? '' : 's'}`;

  const stageChoices = summary
    ? [...summary.stages.stages.map((x) => ({ id: x.id as string | null, name: x.name })), { id: null, name: 'No stage' }]
    : undefined;
  const editBill = (bomId: string) =>
    bomV2Service
      .getBom(bomId)
      .then(setEditing)
      .catch(() => setNotice('Could not open the BOM details. Try again.'));
  const moveToStage = async (rows: ProductRow[], stageId: string | null) => {
    const name = stageChoices?.find((x) => x.id === stageId)?.name ?? 'No stage';
    const moving = rows.filter((r) => (r.stageId ?? null) !== stageId);
    const failed = await eachSettled(moving, (r) => portfolioService.setStage(r.bomId, stageId as StageId | null));
    refreshPortfolio();
    const moved = moving.length - failed.length;
    if (!failed.length) setNotice(rows.length === 1 ? `${rows[0].name} moved to ${name}` : `${many(rows.length)} moved to ${name}`);
    else if (rows.length === 1) setNotice(failed[0].message || 'Could not move the BOM. Try again.');
    else setNotice(`${many(moved)} moved to ${name}; ${failed.length} not moved: ${failed[0].item.name}: ${failed[0].message}`);
  };
  const exportBills = async (rows: ProductRow[], kind: BomExportKind) => {
    setNotice(rows.length === 1 ? `Exporting ${rows[0].name}…` : `Exporting ${many(rows.length)}…`);
    const failed: string[] = [];
    // One file each, one after another, so the browser keeps every download.
    for (const r of rows) {
      try {
        await exportBomToExcel(r.bomId, kind);
      } catch (err) {
        failed.push(`${r.name}: ${(err as Error)?.message || 'unknown error'}`);
      }
    }
    setNotice(failed.length ? `Export failed for ${failed.join('; ')}` : null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 4 }}>
      {/* Title and actions */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 1.5 }}>
        <Box>
          <Typography component="h1" sx={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', color: T.ink }}>{stage === 'ALL' ? 'Portfolio' : stageName}</Typography>
          {summary && <Typography sx={{ fontSize: 13.5, color: T.muted }}>{summary.stages.total} products</Typography>}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Button startIcon={<TrendingUpIcon />} onClick={() => navigate('/bom/analytics')} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 14, height: 40, px: 1.75, borderRadius: 999, color: T.ink2, '&:hover': { bgcolor: T.soft } }}>
          Analytics
        </Button>
        <Button variant="outlined" onClick={() => navigate('/bom-intelligence/upload')} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 14, height: 40, px: 2.25, borderRadius: 999, bgcolor: '#fff', color: T.ink, borderColor: T.line, '&:hover': { borderColor: '#CBD5E1', bgcolor: '#fff' } }}>
          Import BOM
        </Button>
        <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => navigate('/bom/new')} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 14, height: 40, px: 2.5, borderRadius: 999, bgcolor: T.navy, boxShadow: '0 2px 8px rgba(30,58,95,0.28)', '&:hover': { bgcolor: alpha(T.navy, 0.92), boxShadow: '0 2px 8px rgba(30,58,95,0.28)' } }}>
          Create BOM
        </Button>
      </Stack>

      {/* The stage: it sets what the answers and the products below show. */}
      {stageItems.length > 0 && <StageBar value={stage} items={stageItems} onChange={(k) => setView({ stage: k })} />}

      {stage !== 'ALL' && stage !== 'UNSET' && <PillarTabs value={pillar} onChange={(v) => setView({ pillar: v as Pillar })} />}

      {isLoading || !summary ? (
        <Box sx={{ py: 10, display: 'grid', placeItems: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        // Five answers, the same questions in every stage.
        <HeadlinePanel cards={summary.headline} />
      )}

      {/* Every product in view, to explore */}
      <Stack spacing={1} sx={{ mt: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {onScreen.length > 0 && (
              <Box sx={{ ml: '-1px', mr: 0.5, display: 'flex' }}>
                <SelectTick
                  checked={allTicked}
                  some={selected.size > 0 && !allTicked}
                  label={selected.size ? 'Clear the selection' : 'Select all on screen'}
                  onToggle={() => (selected.size ? clearTicks() : setSelected(new Set(onScreen)))}
                />
              </Box>
            )}
            <Typography component="h2" sx={{ fontSize: 17, fontWeight: 700, color: T.ink }}>Products</Typography>
            {products && <Typography sx={{ fontFamily: T.mono, fontSize: 13, color: T.faint }}>{products.total}</Typography>}
            {selected.size > 0 && (
              <>
                <Typography sx={{ fontSize: 13, color: T.muted, pl: 1 }}>
                  <Box component="span" sx={{ fontFamily: T.mono, color: T.ink, fontWeight: 600 }}>{selected.size}</Box> selected
                </Typography>
                <ButtonBase onClick={clearTicks} sx={{ fontSize: 13, fontWeight: 500, color: T.navy, px: 0.75, height: 26, borderRadius: 1.5, '&:hover': { bgcolor: T.soft } }}>
                  Clear
                </ButtonBase>
              </>
            )}
            {productsLoading && <CircularProgress size={12} sx={{ color: T.faint, alignSelf: 'center' }} />}
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          <SearchField
            value={search}
            onChange={(next) => {
              setSearch(next);
              setPage(1);
            }}
            placeholder="Search name, number, contractor, step"
            label="Search products"
          />
          <OrganizeButton
            settings={organize}
            onChange={setOrganize}
            favoritesCount={products?.favoritesCount}
            previewPending={preview}
            onPreviewPending={setPreview}
          />
        </Stack>
        {folders && groupBy ? (
          // Folders: a product with two labels sits in both; "No label" last, folded at first.
          <Stack spacing={1}>
            {folders.some((g) => g.parts != null) && <FolderColumns />}
            {folders.map((g) => {
              const open = !isFolded(groupBy, g.key);
              return (
                <Stack key={g.key} spacing={1}>
                  <FolderHeader
                    name={folderName(groupBy, g)}
                    count={g.count}
                    notSourced={g.notSourced}
                    open={open}
                    muted={g.key === NO_GROUP_KEY}
                    onToggle={() => toggleFold(groupBy, g.key)}
                    stats={g.parts != null ? { parts: g.parts, sourced: g.sourced ?? 0, late: g.late ?? 0, value: g.value ?? '–' } : undefined}
                  />
                  {open && <Stack spacing={0.75} sx={{ pl: 4, pb: 0.5 }}>{g.rows.map(renderRow)}</Stack>}
                </Stack>
              );
            })}
          </Stack>
        ) : (
          (products?.rows ?? []).map(renderRow)
        )}
        {products && !products.total && favorites && !q && !pn && (
          <FavoritesEmpty noun="product" onShowAll={() => setOrganize({ favoritesOnly: false })} />
        )}
        {products && !products.total && !(favorites && !q && !pn) && (
          <Typography sx={{ py: 4, textAlign: 'center', fontSize: 14, color: T.muted }}>{q || pn ? 'No product matches' : 'No products here yet'}</Typography>
        )}
        {products && products.total > 0 && !groupBy && (
          <Stack direction="row" alignItems="center" spacing={2} sx={{ pt: 1.5 }}>
            <Typography sx={{ fontSize: 13, color: T.muted }}>
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, products.total)} of {products.total}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontSize: 13, color: T.muted }}>Rows</Typography>
              <Select
                size="small"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                sx={{ fontSize: 13, height: 32, '& .MuiSelect-select': { py: 0.5 } }}
              >
                {PAGE_SIZES.map((n) => (
                  <MenuItem key={n} value={n} sx={{ fontSize: 13 }}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            {products.total > pageSize && (
              <Pagination
                size="small"
                shape="rounded"
                count={Math.ceil(products.total / pageSize)}
                page={page}
                onChange={(_e, p) => setPage(p)}
              />
            )}
          </Stack>
        )}
      </Stack>

      {menu && (
        <BomRowMenu
          anchorEl={menu.anchor}
          onClose={() => setMenu(null)}
          items={targets.map((t) => ({ bomId: t.bomId, labels: t.labels ?? [] }))}
          openHref={`${billHref}/${menu.row.bomId}`}
          onEdit={() => editBill(menu.row.bomId)}
          onExport={(kind) => exportBills(targets, kind)}
          onDelete={() => (targets.length > 1 ? setDeletingMany(targets) : setDeleting(menu.row))}
          stages={stageChoices}
          currentStage={sharedStage}
          onMoveToStage={(stageId) => moveToStage(targets, stageId)}
        />
      )}

      {deletingMany && (
        <DeleteBomsDialog
          open
          boms={deletingMany.map((r) => ({ bomId: r.bomId, name: r.name }))}
          onClose={() => setDeletingMany(null)}
          onDone={(deletedIds) => {
            refreshPortfolio();
            if (deletedIds.length === deletingMany.length) {
              setNotice(`${many(deletedIds.length)} deleted`);
              setDeletingMany(null);
            }
          }}
        />
      )}

      {editing && (
        <EditBomDialog
          open
          bomId={editing.bomId}
          bom={editing}
          initialData={{
            name: editing.bomName,
            description: editing.description,
            status: editing.lifecycleStatus,
            bomType: editing.bomType,
            equipmentType: editing.equipment?.equipmentType,
            equipmentModel: editing.equipment?.equipmentModel,
            notes: editing.notes,
            tags: editing.tags ?? [],
          }}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refreshPortfolio();
          }}
          onBehaviourChanged={refreshPortfolio}
        />
      )}

      {deleting && (
        <DeleteBomDialog
          open
          bomId={deleting.bomId}
          bomName={deleting.name}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setNotice(`${deleting.name} deleted`);
            setDeleting(null);
            refreshPortfolio();
          }}
        />
      )}

      <Snackbar
        open={!!notice}
        message={notice}
        autoHideDuration={notice?.startsWith('Exporting') ? null : 4000}
        onClose={(_e, reason) => {
          if (reason !== 'clickaway') setNotice(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default BomPortfolioPage;
